import axiosClient from "../../services/apiClient";
import {
  mockWellnessPresence,
  mockWellnessLastSession,
} from "../../services/devMocks";
import { parseUtc } from "../../services/parseUtc";

/*
 * TIMESTAMPS IN THIS MODULE (cts_app_web#62, settled 2026-11-03 by measurement).
 *
 * Every timestamp the API sends -- telemetry `datetime`, session `dateStart` -- is
 * UTC WITH NO ZONE DESIGNATOR. So it goes through `parseUtc`, which appends the `Z`
 * the wire omits. These ten sites used a bare `Date.parse`, which JavaScript reads
 * as BROWSER-LOCAL, putting every event five hours out for a viewer in US Eastern
 * and moving events across local midnight into the wrong day bucket.
 *
 * HOW IT WAS ESTABLISHED, because the comments here previously asserted the
 * opposite and were believed for two weeks. Each row carries the device's own claim
 * as `datetime` and the IoT broker's receive time as `createdDatetime`, which
 * `CtsScsAmbient_v1` and its siblings build with
 * `parse_time("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", timestamp())` -- UTC by construction,
 * since the format string carries a literal Z. Comparing the two over 40,000
 * telemetry rows and 20,365 session rows put 99.99% within two minutes of each
 * other, with a modal difference of zero. The sample spans six US DST transitions:
 * were these values device-local, the difference would sit near -14400s or -18000s
 * and STEP BY AN HOUR across each March and November boundary. The monthly median
 * is 0 or -1 in all 34 months. The firmware agrees independently --
 * `cts_scs_get_time` is `gmtime_r` + `"%FT%H:%M:%S"`.
 *
 * TWO THINGS THE OLD COMMENTS GOT WRONG, both worth knowing:
 *   - They described a "space-separated" format. Zero of those 40,000 rows use a
 *     space; all are `T`-separated. The `.replace(" ", "T")` these calls performed
 *     was a no-op on real data, and the bug was never about the separator -- it was
 *     the missing zone, which would bite identically either way.
 *   - They treated `AmbientTemperatureReading.measuredAt` as a second, UTC
 *     convention sitting beside this local one. It is not a second field:
 *     `homeTemperatureThreshold.getAmbientTemperature` returns
 *     `measuredAt: first.datetime`. One value, read two ways, one of them wrong.
 *
 * THE DATE-PICKER BOUNDARIES BELOW STAY LOCAL, deliberately. `Date.parse` on
 * `${startDate}T00:00:00` is a local-midnight boundary because a user picking
 * "Oct 30" means their own calendar day, not 00:00Z. Instants are absolute;
 * filtering and day-bucketing are local-calendar operations. Do not "fix" those to
 * UTC to match -- that would shift the filter window by the viewer's offset.
 */

const groupPageSize = 200;

async function getOrThrow(url) {
  const response = await axiosClient.get(url);
  if (response.status !== 200) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.data;
}

// DynamoDB PartiQL (used server-side for sensor-group sensors) can return
// fewer than PageSize items with a nextPageToken. Walk every page so the
// caller sees the full sensor list.
const MAX_PAGES = 50;

async function getAllPages(baseUrl) {
  const merged = { data: [], nextPageToken: "", size: 0 };
  let token = "";
  const separator = baseUrl.includes("?") ? "&" : "?";
  let guard = 0;
  for (; guard < MAX_PAGES; guard += 1) {
    const url = token
      ? `${baseUrl}${separator}nextPageToken=${encodeURIComponent(token)}`
      : baseUrl;
    const page = await getOrThrow(url);
    if (page?.data?.length) merged.data.push(...page.data);
    token = page?.nextPageToken || "";
    if (!token) break;
  }
  // Hit the guard with more pages available — the caller is seeing a
  // truncated list. Surface it in the console so it does not silently
  // mask a genuinely too-large group.
  if (guard === MAX_PAGES && token) {
    console.warn(
      `getAllPages: stopped after ${MAX_PAGES} pages while more remain (${baseUrl}); result is truncated.`
    );
  }
  merged.size = merged.data.length;
  return merged;
}

export async function getSensorSessions(
  sensorName,
  sensorPageSize,
  dateTimeStart,
  dateTimeEnd
) {
  const params = [`pageSize=${sensorPageSize}`];
  if (dateTimeStart) params.push(`dateTimeStart=${dateTimeStart}`);
  if (dateTimeEnd) params.push(`dateTimeEnd=${dateTimeEnd}`);
  const result = await getOrThrow(
    `telemetry/${sensorName}/sessions?${params.join("&")}`
  );
  // No-op unless REACT_APP_MOCK_WELLNESS is set, and only for the single-row
  // "latest session" query: ageing a row inside a history fetch would distort
  // the session charts.
  return mockWellnessLastSession(sensorName, sensorPageSize, result);
}

/**
 * Connection/disconnection transitions for a sensor, newest first.
 *
 * Serves two different questions and the caller decides which: read with
 * pageSize 1 it answers "online right now", which is live state and must NOT be
 * clipped to the sensor's current location; read with a large page size it is
 * outage history, which must be. See scopedTelemetry for the rule.
 */
export async function getSensorConnectivity(sensorName, sensorPageSize) {
  return getOrThrow(
    `telemetry/${sensorName}/connectivity?pageSize=${sensorPageSize}`
  );
}

export async function getSensorStateChanges(sensorName, sensorPageSize) {
  return getOrThrow(
    `telemetry/${sensorName}/statechange?pageSize=${sensorPageSize}`
  );
}

export async function getPCOStateChanges(
  sensorName,
  sensorPageSize,
  dateTimeStart,
  dateTimeEnd
) {
  const url =
    dateTimeStart && dateTimeEnd
      ? `telemetry/${sensorName}/PCOstatechange?pageSize=${sensorPageSize}&dateTimeStart=${dateTimeStart}&dateTimeEnd=${dateTimeEnd}`
      : `telemetry/${sensorName}/PCOstatechange?pageSize=${sensorPageSize}`;
  return getOrThrow(url);
}

export async function getBootInfo(sensorName, sensorPageSize) {
  return getOrThrow(
    `telemetry/${sensorName}/bootinfo?pageSize=${sensorPageSize}`
  );
}

/**
 * Every sensor group (property) belonging to the CURRENT tenant.
 *
 * `cts_sensor_groups` is keyed on tenantId, so this list is genuinely
 * tenant-scoped -- unlike installations and telemetry, which are keyed on the
 * sensor serial alone and come back across owners. That makes this the only
 * signal a client has for deciding which of a sensor's past locations belong to
 * the organization looking at it.
 *
 * Paginated deliberately. The portfolio used to read a single page of ten,
 * which merely truncated a long property list; now that ownership is decided
 * against this list, a missing page would hide an organization's own history.
 */
export async function getSensorGroups() {
  return getAllPages(`sensor-groups?pageSize=${groupPageSize}`);
}

/**
 * Every sensor in the tenant, paged to exhaustion.
 *
 * One request where walking properties would be one per property -- and, more to
 * the point, it finds a sensor without the caller having to know which property it
 * is at. Scope-filtered server-side, so an admin gets all of them and anybody else
 * gets what they may reach.
 */
export async function getAllSensors() {
  return getAllPages(`sensors?pageSize=${groupPageSize}`);
}

export async function getSensors(groupid) {
  return getAllPages(
    `sensor-groups/${groupid}/sensors?pageSize=${groupPageSize}`
  );
}

/**
 * Every installation record for a sensor, newest first.
 *
 * `cts_scs_installations` is HASH `scsName` / RANGE `installDatetime`, and the
 * API's repository implements only Add -- Update and Delete both throw -- so
 * this is an append-only per-sensor history. A row is written on first install
 * AND on every re-home, because `POST /api/installations` unlinks the sensor
 * from its old groups before appending the row.
 *
 * What it does NOT record:
 *   - the sensor group's id. The row stores `address`, which the API flattens
 *     to `"<groupName> - <displayName>"` at write time. It is a label and
 *     cannot be resolved back to a building.
 *   - a re-home performed by editing sensor-group membership directly
 *     (`POST/DELETE /api/sensor-groups/{id}/sensors`). Those endpoints write no
 *     installation row, so some moves leave no trace here at all.
 *
 * Paged like every other list endpoint, so walk it: a sensor re-provisioned
 * during a service visit can accumulate more rows than one page holds.
 */
const installationPageSize = 50;

export async function getInstallations(
  sensorName,
  pageSize = installationPageSize
) {
  return getAllPages(`installations/${sensorName}?pageSize=${pageSize}`);
}

/**
 * Fetches the latest N presence-detection events for a sensor. Each row in
 * the underlying `cts_scs_telemetry` table is a single presence detection,
 * so `pageSize=1` is enough to learn when presence was last seen.
 */
export async function getSensorPresence(sensorName, pageSize = 1) {
  const result = await getOrThrow(
    `telemetry/${sensorName}/presence?pageSize=${pageSize}`
  );
  // No-op unless REACT_APP_MOCK_WELLNESS is set (dev preview only).
  return mockWellnessPresence(sensorName, pageSize, result);
}

/**
 * Fetches the latest N ambient (home) temperature readings for a sensor.
 * pageSize=1 is enough to show the current value on the dashboard.
 */
export async function getSensorAmbientTemperature(sensorName, pageSize = 1) {
  return getOrThrow(
    `telemetry/${sensorName}/ambient-temperature?pageSize=${pageSize}`
  );
}

/**
 * Fetches the latest N alert snapshots for a sensor. Each record exposes
 * `currentAlarms` and `prevAlarms` arrays (server-side names). A new alert
 * is a string in currentAlarms that wasn't in prevAlarms. Endpoint accepts
 * pageSize only; date filtering is done client-side off `datetime`.
 */
export async function getSensorAlarms(sensorName, pageSize = 500) {
  return getOrThrow(`telemetry/${sensorName}/alarms_v2?pageSize=${pageSize}`);
}

export const calcTotalSessionsDuration = (sessions) => {
  let totalDuration = 0;
  sessions.forEach((session) => {
    totalDuration += session.durationSeconds;
  });
  return Math.round(totalDuration * 100) / 100;
};

export const calcSessionPresenceDuration = (sessions) => {
  let presenceDuration = 0;
  sessions.forEach((session) => {
    presenceDuration += session.presence.durationSeconds;
  });
  return Math.round(presenceDuration * 100) / 100;
};

export const calcSessionExposedDuration = (sessions) => {
  let exposedDuration = 0;
  sessions.forEach((session) => {
    exposedDuration += session.exposedBurner.durationSeconds;
  });
  return Math.round(exposedDuration * 100) / 100;
};

export const calcSessionsTooHotPotDuration = (sessions) => {
  let tooHotPotDuration = 0;
  sessions.forEach((session) => {
    tooHotPotDuration += session.tooHotPot.durationSeconds;
  });
  return Math.round(tooHotPotDuration * 100) / 100;
};

export const calcSessionPresenceCount = (sessions) => {
  let presenceCount = 0;
  sessions.forEach((session) => {
    presenceCount += session.presence.count;
  });
  return presenceCount;
};

/**
 * Compute the share of cooking time during which presence was detected.
 * Returns `{ presentSeconds, totalSeconds, ratio }`. Ratio is null when
 * there's no cooking at all (vs. 0 when there's cooking but no presence)
 * so the UI can distinguish "no data" from "0%".
 */
export function calcPresenceRatio(sessions) {
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return { presentSeconds: 0, totalSeconds: 0, ratio: null };
  }
  let present = 0;
  let total = 0;
  for (const s of sessions) {
    total += s?.durationSeconds ?? 0;
    present += s?.presence?.durationSeconds ?? 0;
  }
  return {
    presentSeconds: present,
    totalSeconds: total,
    ratio: total > 0 ? present / total : null,
  };
}

export const calcSessionFireCount = (sessions) => {
  let fireCount = 0;
  sessions.forEach((session) => {
    fireCount += session.fire.count;
  });
  return fireCount;
};

/**
 * The dashboard counts these three real-world danger transitions as alerts,
 * matching the mobile app's notification set (minus `isStoveShutoff`, which is
 * a control action, not a danger event).
 *
 * `isCameraObstructed` is also absent, and stays absent: the firmware still
 * raises it, but it is a tamper/current-state condition rather than a cooking
 * danger, so counting it would skew the 28-day danger trend and the alert
 * charts. Nothing surfaces it any more, so today it is simply ignored.
 */
export const ALERT_TYPES = [
  "isTooHot",
  "isUnattended",
  "isImmediateDanger",
  "isHomeTempOutOfRange",
];

/**
 * Derive the unified Stove Control state for a single sensor row. Maps
 * connectivity / PCO / stove / lock fields into one (badge, action) pair so
 * the Sensors table cell and the Sensor Detail header use the same logic.
 *
 * Row shape:
 *  - Status:                "Online" | "Offline"
 *  - isPCOconnected:        "YES" | "NO" | "NULL"
 *  - stoveStatus:           "ON" | "OFF" | "NULL"
 *  - IsStoveRemoteUnlocked: "LOCKED" | "UNLOCKED" | "NULL"
 */
export function deriveStoveControl(row) {
  const online = row.Status === "Online";
  const pco = row.isPCOconnected === "YES";

  if (!online) {
    return {
      tone: "muted",
      label: "Unavailable",
      longLabel: "Sensor Offline",
      action: null,
      tooltip: "Sensor is offline.",
    };
  }
  if (!pco) {
    return {
      tone: "muted",
      label: "N/A",
      longLabel: "No Power Cutoff",
      action: null,
      tooltip: "No power cutoff unit attached to this sensor.",
    };
  }
  if (
    row.stoveStatus === "OFF" &&
    row.IsStoveRemoteUnlocked === "LOCKED"
  ) {
    // Unlock is a recovery/restoring-access action — blue, not warning.
    return {
      tone: "info",
      label: "Locked",
      action: "unlock",
      actionLabel: "Unlock Stove",
      tooltip:
        "Stove was disabled remotely and is locked. Click Unlock Stove to allow it to be powered on at the appliance.",
    };
  }
  if (row.stoveStatus === "OFF") {
    // Power is disabled but the stove isn't locked; re-asserting the lock
    // restores a safe state, so this reads blue (recovery), not red.
    return {
      tone: "info",
      label: "Power disabled",
      action: "lock",
      actionLabel: "Lock Stove",
      tooltip:
        "Stove power is off but not locked. Click Lock Stove to lock it from being turned on.",
    };
  }
  // Stove is on / cooking — destructive cut-power-and-lock action.
  return {
    tone: "danger",
    label: "Cooking",
    action: "shutoff",
    actionLabel: "Disable & Lock",
    tooltip:
      "Cuts power to the stove and locks it from being turned on.",
  };
}

const SHUTOFF_TYPE = "isStoveShutoff";

/**
 * Count alarm-triggered stove shutoffs in the window. A shutoff event is a
 * transition where `isStoveShutoff` enters currentAlarms simultaneously with
 * any of the danger alarm types. That means the PCO cut power because of an
 * alarm, not because of a manual dashboard action.
 */
export function countAlarmShutoffs(alertRecords, startDate, endDate) {
  if (!Array.isArray(alertRecords) || alertRecords.length === 0) return 0;
  const startMs = startDate ? Date.parse(`${startDate}T00:00:00`) : -Infinity;
  const endMs = endDate ? Date.parse(`${endDate}T23:59:59`) : Infinity;
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;

  let count = 0;
  for (const rec of alertRecords) {
    if (!rec || !rec.datetime) continue;
    const ts = parseUtc(rec.datetime);
    if (ts === null || ts < startMs || ts > endMs) continue;
    const curr = rec.currentAlarms || rec.currAlarms || [];
    const prev = rec.prevAlarms || [];
    if (!curr.includes(SHUTOFF_TYPE) || prev.includes(SHUTOFF_TYPE)) continue;
    // Require a coexisting danger alarm so we count automatic, not manual, shutoffs.
    if (curr.some((t) => ALERT_TYPES.includes(t))) count += 1;
  }
  return count;
}

/**
 * Bucket alert + auto-shutoff transitions into a time series for charting.
 * Range presets are relative to "now"; bucket size scales with range length so
 * bars stay legible at any zoom.
 *
 * @param {Array}  records raw alarms_v2 rows (newest-first as returned by API)
 * @param {"week"|"month"|"3month"|"year"|"all"} range
 * @returns {Array<{bucket:string,label:string,isUnattended:number,isTooHot:number,isImmediateDanger:number,isStoveShutoff:number}>}
 */
export function bucketAlertTransitions(records, range) {
  const now = new Date();
  let start; // earliest bucket boundary
  let bucket; // "day" | "week" | "month"

  switch (range) {
    case "week":
      start = new Date(now);
      start.setDate(start.getDate() - 6);
      bucket = "day";
      break;
    case "month":
      start = new Date(now);
      start.setDate(start.getDate() - 29);
      bucket = "day";
      break;
    case "3month":
      start = new Date(now);
      start.setDate(start.getDate() - 7 * 12);
      bucket = "week";
      break;
    case "year":
      start = new Date(now);
      start.setMonth(start.getMonth() - 11);
      bucket = "month";
      break;
    case "all":
    default:
      // Earliest record dictates start; fall back to ~2y if no data.
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 2);
      if (Array.isArray(records) && records.length > 0) {
        let earliest = Infinity;
        for (const rec of records) {
          if (!rec?.datetime) continue;
          const ts = parseUtc(rec.datetime);
          if (ts !== null && ts < earliest) earliest = ts;
        }
        if (earliest !== Infinity) start = new Date(earliest);
      }
      bucket = "month";
      break;
  }

  // Normalize start to bucket boundary.
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const startOfWeek = (d) => {
    // Monday-anchored week.
    const x = startOfDay(d);
    const day = (x.getDay() + 6) % 7; // Mon=0
    x.setDate(x.getDate() - day);
    return x;
  };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

  const floor = bucket === "day" ? startOfDay : bucket === "week" ? startOfWeek : startOfMonth;
  const advance = (d) => {
    const x = new Date(d);
    if (bucket === "day") x.setDate(x.getDate() + 1);
    else if (bucket === "week") x.setDate(x.getDate() + 7);
    else x.setMonth(x.getMonth() + 1);
    return x;
  };

  const fmt = (d) => {
    if (bucket === "day") {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    }
    if (bucket === "week") {
      // ISO-ish "yyyy-MM-dd" of week start
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const labelFmt = (d) => {
    if (bucket === "day") {
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    }
    if (bucket === "week") {
      return `Wk ${d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })}`;
    }
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };

  // Pre-create empty buckets covering the full window.
  const buckets = new Map();
  for (let cur = floor(start); cur <= now; cur = advance(cur)) {
    buckets.set(fmt(cur), {
      bucket: fmt(cur),
      label: labelFmt(cur),
      isUnattended: 0,
      isTooHot: 0,
      isImmediateDanger: 0,
      isStoveShutoff: 0,
    });
  }

  if (!Array.isArray(records)) return Array.from(buckets.values());

  const startMs = floor(start).getTime();

  for (const rec of records) {
    if (!rec?.datetime) continue;
    const ts = parseUtc(rec.datetime);
    if (ts === null || ts < startMs) continue;
    const key = fmt(floor(new Date(ts)));
    const slot = buckets.get(key);
    if (!slot) continue;

    const curr = rec.currentAlarms || rec.currAlarms || [];
    const prev = rec.prevAlarms || [];
    for (const t of curr) {
      if (prev.includes(t)) continue;
      if (ALERT_TYPES.includes(t)) {
        slot[t] += 1;
      } else if (
        t === SHUTOFF_TYPE &&
        curr.some((other) => ALERT_TYPES.includes(other))
      ) {
        slot.isStoveShutoff += 1;
      }
    }
  }

  return Array.from(buckets.values());
}

/**
 * Bucket cooking sessions into the same time-series shape as
 * `bucketAlertTransitions`. Sessions arrive newest-first with `dateStart`
 * (ISO). Returns one entry per bucket covering the chosen range with a
 * `sessions` count.
 *
 * @param {Array} sessions
 * @param {"week"|"month"|"3month"|"year"|"all"} range
 */
export function bucketSessions(sessions, range) {
  const now = new Date();
  let start;
  let bucket;
  switch (range) {
    case "week":
      start = new Date(now);
      start.setDate(start.getDate() - 6);
      bucket = "day";
      break;
    case "month":
      start = new Date(now);
      start.setDate(start.getDate() - 29);
      bucket = "day";
      break;
    case "3month":
      start = new Date(now);
      start.setDate(start.getDate() - 7 * 12);
      bucket = "week";
      break;
    case "year":
      start = new Date(now);
      start.setMonth(start.getMonth() - 11);
      bucket = "month";
      break;
    case "all":
    default:
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 2);
      if (Array.isArray(sessions) && sessions.length > 0) {
        let earliest = Infinity;
        for (const s of sessions) {
          const raw = s?.dateStart;
          if (!raw) continue;
          const ts = parseUtc(raw);
          if (ts !== null && ts < earliest) earliest = ts;
        }
        if (earliest !== Infinity) start = new Date(earliest);
      }
      bucket = "month";
      break;
  }

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const startOfWeek = (d) => {
    const x = startOfDay(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    return x;
  };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const floor =
    bucket === "day" ? startOfDay : bucket === "week" ? startOfWeek : startOfMonth;
  const advance = (d) => {
    const x = new Date(d);
    if (bucket === "day") x.setDate(x.getDate() + 1);
    else if (bucket === "week") x.setDate(x.getDate() + 7);
    else x.setMonth(x.getMonth() + 1);
    return x;
  };
  const fmtKey = (d) =>
    bucket === "month"
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`;
  const labelFmt = (d) => {
    if (bucket === "day")
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    if (bucket === "week")
      return `Wk ${d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })}`;
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };

  const buckets = new Map();
  for (let cur = floor(start); cur <= now; cur = advance(cur)) {
    buckets.set(fmtKey(cur), {
      bucket: fmtKey(cur),
      label: labelFmt(cur),
      sessions: 0,
    });
  }
  if (!Array.isArray(sessions)) return Array.from(buckets.values());

  const startMs = floor(start).getTime();
  for (const s of sessions) {
    const raw = s?.dateStart;
    if (!raw) continue;
    const ts = parseUtc(raw);
    if (ts === null || ts < startMs) continue;
    const key = fmtKey(floor(new Date(ts)));
    const slot = buckets.get(key);
    if (slot) slot.sessions += 1;
  }
  return Array.from(buckets.values());
}

/**
 * Bucket cooking sessions by day-of-week × hour-of-day.
 * Returns `m[dow][hour]` (Mon=0). A session counts in the hour it started.
 */
export function bucketSessionsByHourWeek(sessions, range) {
  const matrix = Array.from({ length: 7 }, () => new Array(24).fill(0));
  if (!Array.isArray(sessions) || sessions.length === 0) return matrix;

  const now = Date.now();
  let startMs = -Infinity;
  switch (range) {
    case "week":
      startMs = now - 6 * 86400000;
      break;
    case "month":
      startMs = now - 29 * 86400000;
      break;
    case "3month":
      startMs = now - 7 * 12 * 86400000;
      break;
    case "year": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 11);
      startMs = d.getTime();
      break;
    }
    case "all":
    default:
      startMs = -Infinity;
      break;
  }

  for (const s of sessions) {
    const raw = s?.dateStart;
    if (!raw) continue;
    const ts = parseUtc(raw);
    if (ts === null || ts < startMs) continue;
    const d = new Date(ts);
    const dow = (d.getDay() + 6) % 7;
    matrix[dow][d.getHours()] += 1;
  }
  return matrix;
}

/**
 * Bucket alert transitions by day-of-week × hour-of-day for the given range.
 * Returns a 7×24 matrix `m[dow][hour]` where dow=0 is Monday.
 * Counts only the three danger ALERT_TYPES (no shutoffs).
 *
 * @param {Array} alertRecords
 * @param {"week"|"month"|"3month"|"year"|"all"} range
 */
export function bucketAlertsByHourWeek(alertRecords, range) {
  const matrix = Array.from({ length: 7 }, () => new Array(24).fill(0));
  if (!Array.isArray(alertRecords) || alertRecords.length === 0) return matrix;

  const now = new Date();
  let startMs = -Infinity;
  switch (range) {
    case "week":
      startMs = now.getTime() - 6 * 86400000;
      break;
    case "month":
      startMs = now.getTime() - 29 * 86400000;
      break;
    case "3month":
      startMs = now.getTime() - 7 * 12 * 86400000;
      break;
    case "year": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 11);
      startMs = d.getTime();
      break;
    }
    case "all":
    default:
      startMs = -Infinity;
      break;
  }

  for (const rec of alertRecords) {
    if (!rec?.datetime) continue;
    const ts = parseUtc(rec.datetime);
    if (ts === null || ts < startMs) continue;
    const curr = rec.currentAlarms || rec.currAlarms || [];
    const prev = rec.prevAlarms || [];
    let increment = 0;
    for (const t of curr) {
      if (ALERT_TYPES.includes(t) && !prev.includes(t)) increment += 1;
    }
    if (increment === 0) continue;
    const d = new Date(ts);
    const dow = (d.getDay() + 6) % 7; // Mon=0
    const hour = d.getHours();
    matrix[dow][hour] += increment;
  }
  return matrix;
}

/**
 * Count alert transitions in the given window. A "transition" is a type that
 * appears in currentAlarms but not in prevAlarms on the same record. The
 * alarms_v2 endpoint doesn't support server-side date filtering, so we filter
 * by `datetime` here.
 *
 * @param {Array} alertRecords - rows from getSensorAlarms (most recent first)
 * @param {string|null} startDate - "yyyy-MM-dd"
 * @param {string|null} endDate - "yyyy-MM-dd"
 */
/**
 * Group alert transitions by type within the window. Returns counts for each
 * type in ALERT_TYPES.
 */
export function groupAlertsByType(alertRecords, startDate, endDate) {
  const out = {
    isTooHot: 0,
    isUnattended: 0,
    isImmediateDanger: 0,
    isHomeTempOutOfRange: 0,
  };
  if (!Array.isArray(alertRecords) || alertRecords.length === 0) return out;
  const startMs = startDate ? Date.parse(`${startDate}T00:00:00`) : -Infinity;
  const endMs = endDate ? Date.parse(`${endDate}T23:59:59`) : Infinity;
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return out;

  for (const rec of alertRecords) {
    if (!rec || !rec.datetime) continue;
    const ts = parseUtc(rec.datetime);
    if (ts === null || ts < startMs || ts > endMs) continue;
    const curr = rec.currentAlarms || rec.currAlarms || [];
    const prev = rec.prevAlarms || [];
    for (const t of curr) {
      if (ALERT_TYPES.includes(t) && !prev.includes(t)) out[t] = (out[t] || 0) + 1;
    }
  }
  return out;
}

/**
 * Flatten alert records into individual transition events with timestamp +
 * type, sorted newest-first. Honors date range when provided.
 */
export function listAlertTransitions(alertRecords, startDate, endDate) {
  if (!Array.isArray(alertRecords) || alertRecords.length === 0) return [];
  const startMs = startDate ? Date.parse(`${startDate}T00:00:00`) : -Infinity;
  const endMs = endDate ? Date.parse(`${endDate}T23:59:59`) : Infinity;
  const out = [];
  for (const rec of alertRecords) {
    if (!rec || !rec.datetime) continue;
    const ts = parseUtc(rec.datetime);
    if (ts === null || ts < startMs || ts > endMs) continue;
    const curr = rec.currentAlarms || rec.currAlarms || [];
    const prev = rec.prevAlarms || [];
    for (const t of curr) {
      if (ALERT_TYPES.includes(t) && !prev.includes(t)) {
        out.push({ timestamp: ts, datetime: rec.datetime, type: t });
      }
    }
  }
  out.sort((a, b) => b.timestamp - a.timestamp);
  return out;
}

export function countAlertTransitions(alertRecords, startDate, endDate) {
  if (!Array.isArray(alertRecords) || alertRecords.length === 0) return 0;

  // Build inclusive day-bounded range. End of day on endDate.
  const startMs = startDate ? Date.parse(`${startDate}T00:00:00`) : -Infinity;
  const endMs = endDate ? Date.parse(`${endDate}T23:59:59`) : Infinity;
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;

  let count = 0;
  for (const rec of alertRecords) {
    if (!rec || !rec.datetime) continue;
    const ts = parseUtc(rec.datetime);
    if (ts === null || ts < startMs || ts > endMs) continue;

    const curr = rec.currentAlarms || rec.currAlarms || [];
    const prev = rec.prevAlarms || [];
    for (const t of curr) {
      if (ALERT_TYPES.includes(t) && !prev.includes(t)) count += 1;
    }
  }
  return count;
}

export function buildEventsAllBuildings(propertyData) {
  var output = [];
  for (
    var buildingIndex = 0;
    buildingIndex < propertyData.length;
    buildingIndex++
  ) {
    let oneBuildingSum = {
      buildingId: propertyData[buildingIndex].id, // buildingId
      name: propertyData[buildingIndex].name, // name
      LatLong: [
        propertyData[buildingIndex].location.latitude,
        propertyData[buildingIndex].location.longitude,
      ],
    };

    output = output.concat(oneBuildingSum);
  }
  return output;
}
